import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from '../../styles/case-study-modal.module.scss';
import { TELEGRAM_CTA_URL } from '../../constants/cta';
import CaseStudySlider from './CaseStudySlider';
import CaseStudyInfoPanel from './CaseStudyInfoPanel';
import { trackGoal } from '../../analytics/metrika';

const CLOSE_ANIMATION_DURATION_MS = 340;

/**
 * @typedef {Object} CaseStudyData
 * @property {string=} id
 * @property {string=} title
 * @property {string=} serviceType
 * @property {string=} timeline
 * @property {string=} priceFrom
 * @property {string=} priceRange
 * @property {string=} problem
 * @property {string | string[]=} solution
 * @property {string[]=} deliverables
 * @property {string=} result
 * @property {string=} resultMetric
 * @property {string[]=} tags
 * @property {string[]=} gallery
 * @property {string=} prev
 * @property {string=} full
 * @property {string=} link
 * @property {string=} ctaText
 * @property {string=} niche
 */

const getSliderImages = (caseData) => {
  const raw = caseData?.gallery?.length ? caseData.gallery : [caseData?.prev, caseData?.full];
  const cleaned = raw.filter(Boolean);
  return cleaned.filter((item, index) => cleaned.indexOf(item) === index);
};

/**
 * @param {{
 * isOpen: boolean,
 * caseData?: CaseStudyData | null,
 * onClose: () => void,
 * onTelegramClick?: () => void
 * }} props
 */
export default function CaseStudyModal({ isOpen, caseData, onClose, onTelegramClick }) {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef(null);
  const frameRef = useRef(null);

  const handleTelegramClick = onTelegramClick
    ? onTelegramClick
    : () => {
        trackGoal('cta_telegram_click', {
          source: 'case_modal',
          caseId: caseData?.id || 'unknown',
          caseTitle: caseData?.title || 'unknown',
        });

        window.open(TELEGRAM_CTA_URL, '_blank', 'noopener,noreferrer');
      };

  useEffect(() => {
    if (isOpen) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      setShouldRender(true);
      setIsClosing(false);
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
      return undefined;
    }

    if (!shouldRender) {
      return undefined;
    }

    setIsClosing(true);
    setIsVisible(false);
    closeTimerRef.current = setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
    }, CLOSE_ANIMATION_DURATION_MS);

    return undefined;
  }, [isOpen, shouldRender]);

  useEffect(() => {
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!shouldRender) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [shouldRender, onClose]);

  if (!shouldRender) {
    return null;
  }

  const images = getSliderImages(caseData);

  return (
    <div
      className={clsx(styles.overlay, isVisible && styles.overlayVisible, isClosing && styles.overlayClosing)}
      onClick={onClose}
      role="presentation">
      <div className={styles.modalShell}>
        <section
          className={clsx(styles.modal, isVisible && styles.modalVisible, isClosing && styles.modalClosing)}
          role="dialog"
          aria-modal="true"
          aria-label={`Кейс ${caseData?.title || ''}`.trim()}
          onClick={(event) => {
            event.stopPropagation();
          }}>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Закрыть окно кейса">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6.7 5.3a1 1 0 0 0-1.4 1.4L10.6 12l-5.3 5.3a1 1 0 1 0 1.4 1.4l5.3-5.3l5.3 5.3a1 1 0 1 0 1.4-1.4L13.4 12l5.3-5.3a1 1 0 0 0-1.4-1.4L12 10.6z" />
            </svg>
          </button>

          <div className={styles.contentScroller}>
            <div className={styles.layout}>
              <div className={styles.mediaColumn}>
                <CaseStudySlider images={images} caseData={caseData} title={caseData?.title || 'Кейс'} />
              </div>
              <div className={styles.infoColumn}>
                <CaseStudyInfoPanel caseData={caseData} onTelegramClick={handleTelegramClick} />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
